<template>
  <el-dialog :title="mode === 'create' ? '新增考试' : '编辑考试'" :visible.sync="dialogVisible" width="1000px"
    @close="handleClose">
    <el-form ref="form" :model="form" :rules="rules" label-width="100px" size="small">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="考试标题" prop="title">
            <el-input v-model="form.title" placeholder="请输入考试标题" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="启用状态" prop="enabled">
            <el-switch v-model="form.enabled" active-text="启用" inactive-text="禁用" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="总分" prop="totalScore">
            <el-input-number disabled v-model="form.totalScore" controls-position="right" placeholder="总分"
              style="width: 160px">
              <template slot="append">分</template>
            </el-input-number>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="及格分" prop="passScore">
            <el-input-number v-model="form.passScore" :min="1" :max="form.totalScore || 1000" controls-position="right"
              placeholder="及格分" style="width: 160px" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="考试时长" prop="durationMinutes">
            <el-input-number v-model="form.durationMinutes" :min="1" :max="300" controls-position="right"
              placeholder="不填则不限时" style="width: 160px" />
            <el-button type="text" @click="form.durationMinutes = undefined">不限时</el-button>
            <span class="field-hint">分钟，清空后不限制答题时间</span>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="考试分类" prop="category">
        <el-cascader v-model="form.category" :options="trainingCategoryTree" :props="categoryCascaderProps"
          placeholder="请选择分类" filterable style="width: 100%" />
      </el-form-item>

      <el-form-item label="标签" prop="tags">
        <el-select v-model="form.tags" multiple filterable allow-create default-first-option
          placeholder="输入后回车添加，可多个" style="width: 100%" @change="normalizeTags" />
      </el-form-item>

      <el-form-item label="考试题目">
        <div class="question-section">
          <div class="question-header">
            <div class="question-actions">
              <el-button type="primary" size="small" @click="handleAddQuestion">
                <i class="el-icon-plus"></i>
                添加题目
              </el-button>
              <el-button size="small" @click="handleDownloadTemplate">下载模板</el-button>
              <el-upload class="excel-upload" action="" :show-file-list="false" accept=".xlsx,.xls"
                :http-request="handleImportExcel">
                <el-button type="success" size="small">导入 Excel</el-button>
              </el-upload>
            </div>
            <span class="question-count">题库 {{ form.questions ? form.questions.length : 0 }} 题</span>
          </div>

          <div v-if="form.questions && form.questions.length > 0" class="question-list">
            <div v-for="(question, index) in form.questions" :key="index" class="question-item">
              <div class="question-header">
                <div class="question-info">
                  <span class="question-number">第{{ index + 1 }}题</span>
                  <span v-if="question.category" class="question-category">{{ question.category }}</span>
                  <span class="question-type">
                    {{ QUESTION_TYPE_MAP[question.type] || "未知类型" }}
                  </span>
                  <span class="question-score">{{ question.score }}分</span>
                </div>
                <div class="question-actions">
                  <el-button size="mini" @click="handleEditQuestion(index)">编辑</el-button>
                  <el-button size="mini" type="danger" @click="handleRemoveQuestion(index)">删除</el-button>
                </div>
              </div>

              <div class="question-content">
                <div class="question-text">
                  <div>{{ question.question }}</div>
                  <div v-if="question.medias && question.medias.length > 0" class="question-medias">
                    <file-preview v-for="media in question.medias" :key="media" :value="media" />
                  </div>
                </div>
                <div v-if="question.options && question.options.length > 0" class="question-options">
                  <div v-for="option in question.options" :key="option.id">
                    <div class="option-item" :class="{ 'correct-answer': isCorrectAnswer(question, option.id) }">
                      <span class="option-label">{{ option.id }}.</span>
                      <span class="option-content">{{ option.content }}</span>
                      <i v-if="isCorrectAnswer(question, option.id)" class="el-icon-check correct-icon"></i>
                    </div>
                    <div v-if="option.medias && option.medias.length > 0" class="option-medias">
                      <file-preview v-for="media in option.medias" :key="media" :value="media" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="form.drawRules && form.drawRules.length" class="draw-rules">
            <div class="draw-title">分类抽题</div>
            <div v-for="rule in form.drawRules" :key="rule.category || 'empty'" class="draw-row">
              <span class="draw-category">{{ categoryLabel(rule.category) }}</span>
              <span class="draw-total">共 {{ categorySize(rule.category) }} 题</span>
              <span>抽取</span>
              <el-input-number v-model="rule.count" :min="0" :max="categorySize(rule.category)" size="small"
                @change="updateTotalScore" />
              <span>题</span>
            </div>
            <div class="draw-summary">出题 {{ paperCount }} 题，试卷满分 {{ form.totalScore || 0 }} 分</div>
          </div>
        </div>
      </el-form-item>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="handleClose">取消</el-button>
      <el-button size="small" type="primary" @click="handleConfirm">确定</el-button>
    </div>

    <!-- 题目编辑对话框 -->
    <QuestionDialog :visible.sync="questionDialogVisible" :form-data="currentQuestion" :mode="questionMode"
      :categories="categoryNames" @confirm="handleQuestionConfirm" />
  </el-dialog>
</template>

<script>
import QuestionDialog from "./QuestionDialog.vue";
import { QUESTION_TYPE, QUESTION_TYPE_MAP } from "@/core/const/global-const";
import { saveTrainingExam, downloadTrainingExamTemplate, importTrainingExam } from "@/api/new_training";
import {
  categoryLabel,
  categorySize,
  findMixedScoreCategory,
  hasPartialDraw,
  paperQuestionCount,
  paperTotalScore,
  syncDrawRules
} from "../utils/examPaper";
import _ from "lodash";
import trainingCategoryMixin from "../utils/trainingCategory";
export default {
  name: "ExamDialog",
  mixins: [trainingCategoryMixin],
  components: {
    QuestionDialog
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    formData: {
      type: Object,
      default: () => ({})
    },
    mode: {
      type: String,
      default: "create" // create | edit
    }
  },
  data() {
    return {
      QUESTION_TYPE,
      QUESTION_TYPE_MAP,
      dialogVisible: false,
      form: {
        title: "",
        category: "",
        tags: [],
        passScore: 60,
        totalScore: 100,
        durationMinutes: 10,
        enabled: true,
        questions: [],
        drawRules: []
      },
      rules: {
        title: [{ required: true, message: "请输入考试标题", trigger: "blur" }],
        category: [{ required: true, message: "请选择考试分类", trigger: "change" }],
        passScore: [{ required: true, message: "请输入及格分", trigger: "blur" }]
      },

      // 题目编辑相关
      questionDialogVisible: false,
      questionMode: "create", // create | edit
      currentQuestion: {},
      currentQuestionIndex: -1
    };
  },
  computed: {
    categoryNames() {
      const names = [];
      (this.form.questions || []).forEach((question) => {
        const name = (question.category || "").trim();
        if (name && !names.includes(name)) {
          names.push(name);
        }
      });
      return names;
    },
    paperCount() {
      return paperQuestionCount({
        questions: this.form.questions,
        drawRules: this.form.drawRules
      });
    }
  },
  watch: {
    visible: {
      handler(val) {
        this.dialogVisible = val;
        if (val) {
          this.initForm();
        }
      },
      immediate: true
    }
  },
  methods: {
    // 初始化表单
    initForm() {
      if (this.mode === "create") {
        this.form = {
          title: "",
          category: "",
          tags: [],
          passScore: 60,
          totalScore: 0,
          durationMinutes: 10,
          enabled: true,
          questions: [],
          drawRules: []
        };
      } else {
        const data = _.cloneDeep(this.formData);
        if (!data.drawRules) {
          data.drawRules = [];
        }
        data.tags = Array.isArray(data.tags) ? data.tags.slice() : [];
        data.category = this.normalizeLeafCategory(data.category);
        if (data.durationMinutes == null || data.durationMinutes < 1) {
          data.durationMinutes = undefined;
        }
        this.form = data;
      }
      this.syncDrawRules();
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
      });
    },

    // 添加题目
    handleAddQuestion() {
      this.questionMode = "create";
      this.currentQuestion = {};
      this.currentQuestionIndex = -1;
      this.questionDialogVisible = true;
    },

    // 编辑题目
    handleEditQuestion(index) {
      this.questionMode = "edit";
      this.currentQuestion = _.cloneDeep(this.form.questions[index]);
      this.currentQuestionIndex = index;
      this.questionDialogVisible = true;
    },

    // 删除题目
    handleRemoveQuestion(index) {
      this.$confirm("确定要删除这道题目吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        this.form.questions.splice(index, 1);
        this.syncDrawRules();
      });
    },

    // 题目确认
    handleQuestionConfirm(questionData) {
      if (this.questionMode === "create") {
        this.form.questions.push(questionData);
      } else {
        this.$set(this.form.questions, this.currentQuestionIndex, questionData);
      }
      this.syncDrawRules();
    },

    categoryLabel(category) {
      return categoryLabel(category);
    },

    categorySize(category) {
      return categorySize(this.form.questions, category);
    },

    syncDrawRules() {
      const rules = syncDrawRules(this.form.questions, this.form.drawRules);
      this.$set(this.form, "drawRules", rules);
      this.updateTotalScore();
    },

    // 更新总分。只有少抽了题才按配额计分，抽完全部时仍是题目分值之和。
    updateTotalScore() {
      const totalScore = paperTotalScore(this.form.questions, this.form.drawRules);
      this.$set(this.form, "totalScore", totalScore);
    },

    normalizeTags(values) {
      const seen = {};
      const tags = [];
      (values || []).forEach((item) => {
        String(item || "")
          .split(/[,，、;；]/)
          .forEach((part) => {
            const value = part.trim();
            if (!value || seen[value]) {
              return;
            }
            if (value.length > 30) {
              this.$message.warning("单个标签不能超过30个字");
              return;
            }
            seen[value] = true;
            tags.push(value);
          });
      });
      const current = this.form.tags || [];
      const same = tags.length === current.length && tags.every((item, index) => item === current[index]);
      if (!same) {
        this.form.tags = tags;
      }
    },

    handleDownloadTemplate() {
      downloadTrainingExamTemplate();
    },

    handleImportExcel(request) {
      const run = () => {
        const formData = new FormData();
        formData.append("file", request.file);
        importTrainingExam(formData).then((res) => {
          const data = res.data || {};
          this.form.questions = data.questions || [];
          if (data.tags && data.tags.length) {
            this.form.tags = data.tags.slice();
          }
          if (data.passScore) {
            this.form.passScore = data.passScore;
          }
          this.form.drawRules = data.drawRules || [];
          this.syncDrawRules();
          this.$message.success("导入成功，请确认后保存");
        });
      };
      if (this.form.questions && this.form.questions.length > 0) {
        this.$confirm("导入将覆盖当前题目，是否继续？", "提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning"
        }).then(run);
      } else {
        run();
      }
    },

    // 确认
    handleConfirm() {
      this.$refs.form.validate(async (valid) => {
        if (valid) {
          if (!this.form.questions || this.form.questions.length === 0) {
            this.$message.warning("请至少添加一道题目");
            return;
          }
          if (this.paperCount <= 0) {
            this.$message.warning("请至少抽取一道题");
            return;
          }
          const mixedCategory = findMixedScoreCategory(this.form.questions, this.form.drawRules);
          if (mixedCategory) {
            this.$message.warning(`分类「${mixedCategory}」的分值不一致，同一分类每题分值必须相同`);
            return;
          }
          if (hasPartialDraw(this.form.questions, this.form.drawRules) && this.form.passScore > this.form.totalScore) {
            this.$message.warning("及格分不能大于试卷满分");
            return;
          }

          try {
            const payload = _.cloneDeep(this.form);
            if (!hasPartialDraw(payload.questions, payload.drawRules)) {
              payload.drawRules = [];
            }
            await saveTrainingExam(payload);
            this.$emit("confirm");
            this.handleClose();
          } catch (error) {
            this.$message.error("保存失败");
            console.error(error);
          }
        }
      });
    },

    // 关闭
    handleClose() {
      this.dialogVisible = false;
      this.$emit("update:visible", false);
    },
    isCorrectAnswer(question, optionId) {
      return question.answer && question.answer.includes(optionId);
    }
  }
};
</script>

<style lang="less" scoped>
.score-tip {
  padding-top: 32px;
  color: #409eff;
  font-weight: 600;
}

.field-hint {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}

.question-section {
  .question-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .question-actions {
      display: flex;
      align-items: center;

      .el-button,
      .excel-upload {
        margin-right: 8px;
      }
    }

    .question-count {
      color: #909399;
      font-size: 14px;
    }
  }

  .draw-rules {
    margin-top: 8px;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 6px;

    .draw-title {
      margin-bottom: 10px;
      font-weight: 600;
      color: #303133;
    }

    .draw-row {
      display: flex;
      align-items: center;
      margin-bottom: 8px;

      .draw-category {
        min-width: 88px;
        font-weight: 500;
      }

      .draw-total {
        width: 72px;
        color: #909399;
      }

      .el-input-number {
        margin: 0 8px;
      }
    }

    .draw-summary {
      color: #409eff;
      font-weight: 600;
    }
  }

  .question-list {
    .question-item {
      border: 1px solid #e4e7ed;
      border-radius: 6px;
      margin-bottom: 16px;
      padding: 16px;

      .question-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;

        .question-info {
          display: flex;
          align-items: center;
          margin-bottom: 8px;

          .question-category {
            margin-left: 12px;
            color: #e6a23c;
            font-size: 13px;
          }

          .question-type {
            color: #409eff;
            padding: 2px;
            border-radius: 4px;
            font-size: 13px;
            margin: 0 12px;
            line-height: 30px;
          }

          .question-score {
            color: #67c23a;
            font-weight: 600;
          }
        }

        .question-number {
          font-weight: 600;
          color: #303133;
        }

        .question-actions {
          .el-button {
            margin-left: 8px;
          }
        }
      }

      .question-content {
        .question-text {
          margin-bottom: 12px;
          line-height: 1.6;
          color: #303133;
        }

        .question-medias {
          margin-top: 10px;
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .question-options {
          .option-item {
            display: flex;
            align-items: center;
            margin-bottom: 8px;
            padding: 0 8px;
            border-radius: 4px;
            transition: background-color 0.3s;

            &.correct-answer {
              background-color: #f0f9ff;
              border: 1px solid #67c23a;
            }

            .option-label {
              font-weight: 600;
              margin-right: 8px;
              min-width: 20px;
            }

            .option-content {
              color: #606266;
            }

            .correct-icon {
              color: #67c23a;
              margin-left: 8px;
            }

            .correct-answer {
              background-color: #f0f9eb;
              border-color: #e1f3d8;
            }
          }

          .option-medias {
            margin-left: 26px;
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
          }
        }
      }
    }
  }
}

.dialog-footer {
  text-align: right;
}
</style>
